/**
 * PIN Code Checker — Ultra Legend Edition v3.0.0
 * Shopify Dawn Theme (2025–2026)
 * Real API integrations: PostalPincode, Shiprocket, Delhivery, Ecom Express,
 *   Smartr, DTDC, Blue Dart, XpressBees, Custom List
 * ============================================================
 * NO jQuery dependency. Vanilla JS only.
 * All classes prefixed pcc- → zero theme conflicts.
 * ============================================================
 */

(function () {
  'use strict';

  /* ════════════════════════════════════════════════
   * UTILITY HELPERS
   * ════════════════════════════════════════════════ */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function debounce(fn, delay) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.appendChild(document.createTextNode(String(str)));
    return d.innerHTML;
  }

  function formatDate(date) {
    const opts = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-IN', opts);
  }

  function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  /* ════════════════════════════════════════════════
   * CACHE LAYER (SessionStorage + Memory)
   * ════════════════════════════════════════════════ */

  const MemCache = {
    _store: {},
    set(key, val, ttlMin) {
      this._store[key] = { val, exp: Date.now() + ttlMin * 60000 };
      try {
        sessionStorage.setItem('pcc_cache_' + key, JSON.stringify(this._store[key]));
      } catch (_) { /* quota exceeded */ }
    },
    get(key) {
      let entry = this._store[key];
      if (!entry) {
        try {
          const raw = sessionStorage.getItem('pcc_cache_' + key);
          if (raw) entry = JSON.parse(raw);
        } catch (_) {}
      }
      if (!entry || Date.now() > entry.exp) return null;
      return entry.val;
    },
    clear() {
      this._store = {};
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('pcc_cache_'))
        .forEach(k => sessionStorage.removeItem(k));
    }
  };

  /* ════════════════════════════════════════════════
   * HISTORY MANAGER (localStorage)
   * ════════════════════════════════════════════════ */

  const HistoryMgr = {
    KEY: 'pcc_history',
    load() {
      try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch (_) { return []; }
    },
    save(items) {
      try { localStorage.setItem(this.KEY, JSON.stringify(items)); } catch (_) {}
    },
    add(pin, city, max) {
      const items = this.load().filter(i => i.pin !== pin);
      items.unshift({ pin, city: city || '', ts: Date.now() });
      const trimmed = items.slice(0, max);
      this.save(trimmed);
      return trimmed;
    },
    clear() { localStorage.removeItem(this.KEY); }
  };

  /* ════════════════════════════════════════════════
   * API PROVIDERS
   * Each returns Promise<{ serviceable, city, state, cod, detail, rawData }>
   * ════════════════════════════════════════════════ */

  const API = {

    /* --- India Post / PostalPincode.in (FREE, no key needed) ------------- */
    async postalpincode(pin, _key) {
      const url = `https://api.postalpincode.in/pincode/${pin}`;
      const res = await fetch(url, { method: 'GET', cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const block = data[0];
      if (!block || block.Status === 'Error' || !block.PostOffice?.length) {
        return { serviceable: false, city: '', state: '' };
      }
      const po = block.PostOffice[0];
      return {
        serviceable: true,
        city: po.District || po.Name || '',
        state: po.State || '',
        cod: true,
        detail: `${po.Name}, ${po.District}, ${po.State}`,
        rawData: block
      };
    },

    /* --- Shiprocket -------------------------------------------------------- */
    async shiprocket(pin, key) {
      if (!key) throw new Error('Shiprocket API key missing');
      // Shiprocket serviceability check
      const url = `https://apiv2.shiprocket.in/v1/external/courier/serviceability/?pickup_postcode=110001&delivery_postcode=${pin}&weight=1&cod=1`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`Shiprocket HTTP ${res.status}`);
      const data = await res.json();
      const available = data?.data?.available_courier_companies?.length > 0;
      const company = data?.data?.available_courier_companies?.[0];
      return {
        serviceable: available,
        city: data?.data?.delivery_codes?.[0]?.postal_code?.city || '',
        state: data?.data?.delivery_codes?.[0]?.postal_code?.state_code || '',
        cod: company?.cod === 1,
        detail: company ? `via ${company.courier_name}` : '',
        rawData: data
      };
    },

    /* --- Delhivery --------------------------------------------------------- */
    async delhivery(pin, key) {
      if (!key) throw new Error('Delhivery API key missing');
      const url = `https://track.delhivery.com/api/kinesis/pods/pin-codes/json/?filter_codes=${pin}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Token ${key}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`Delhivery HTTP ${res.status}`);
      const data = await res.json();
      const pinInfo = data?.delivery_codes?.[0]?.postal_code;
      const serviceable = !!pinInfo && pinInfo.active === 'Y';
      return {
        serviceable,
        city: pinInfo?.city || '',
        state: pinInfo?.state_code || '',
        cod: pinInfo?.cod === 'Y',
        detail: serviceable ? `Delhivery serviceable` : '',
        rawData: data
      };
    },

    /* --- Ecom Express ------------------------------------------------------- */
    async ecom_express(pin, key) {
      if (!key) throw new Error('Ecom Express API key missing');
      const url = `https://api.ecomexpress.in/apiv2/pincodes/`;
      const formData = new URLSearchParams({ username: key, pincode: pin });
      const res = await fetch(url, { method: 'POST', body: formData });
      if (!res.ok) throw new Error(`Ecom Express HTTP ${res.status}`);
      const data = await res.json();
      const record = data?.data?.[0];
      return {
        serviceable: data?.success === true && !!record,
        city: record?.city || '',
        state: record?.state || '',
        cod: record?.cod === true,
        detail: record ? `Ecom Express — ${record.city}` : '',
        rawData: data
      };
    },

    /* --- Smartr Logistics -------------------------------------------------- */
    async smartr(pin, key) {
      if (!key) throw new Error('Smartr API key missing');
      const url = `https://carrier.smartr.in/api/v1/pincodes/${pin}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`Smartr HTTP ${res.status}`);
      const data = await res.json();
      return {
        serviceable: data?.serviceable === true,
        city: data?.city || '',
        state: data?.state || '',
        cod: data?.cod === true,
        detail: data?.zone ? `Zone: ${data.zone}` : '',
        rawData: data
      };
    },

    /* --- DTDC -------------------------------------------------------------- */
    async dtdc(pin, key) {
      if (!key) throw new Error('DTDC API key missing');
      const url = `https://dtdc.com/dtdcservice/ServiceabilityCheckService?pinCode=${pin}&apiKey=${key}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`DTDC HTTP ${res.status}`);
      const data = await res.json();
      return {
        serviceable: data?.status === 'serviceable' || data?.isServiceable === true,
        city: data?.city || '',
        state: data?.state || '',
        cod: data?.cod !== false,
        detail: data?.zone ? `Zone ${data.zone}` : '',
        rawData: data
      };
    },

    /* --- Blue Dart --------------------------------------------------------- */
    async bluedart(pin, key) {
      if (!key) throw new Error('Blue Dart API key missing');
      const url = `https://api.bluedart.com/servlet/RoutingServlet?handler=tnt&action=pincode&pinCode=${pin}&LoginID=${key}&licKey=${key}&verno=1.4&format=json`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`BlueDart HTTP ${res.status}`);
      const data = await res.json();
      const info = data?.AWBNo || data?.RoutingResponse;
      return {
        serviceable: !!info,
        city: data?.Destination || '',
        state: data?.DestinationState || '',
        cod: true,
        detail: data?.Destination ? `Blue Dart — ${data.Destination}` : '',
        rawData: data
      };
    },

    /* --- XpressBees -------------------------------------------------------- */
    async xpressbees(pin, key) {
      if (!key) throw new Error('XpressBees API key missing');
      const url = 'https://ship.xpressbees.com/api/pincode/serviceability';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pincode: pin })
      });
      if (!res.ok) throw new Error(`XpressBees HTTP ${res.status}`);
      const data = await res.json();
      return {
        serviceable: data?.status === 200 || data?.data?.serviceable === true,
        city: data?.data?.city || '',
        state: data?.data?.state || '',
        cod: data?.data?.cod !== false,
        detail: data?.data?.zone ? `Zone: ${data.data.zone}` : '',
        rawData: data
      };
    },

    /* --- Custom / Offline -------------------------------------------------- */
    custom(pin, _key, config) {
      const allowed = (config.allowedPins || '')
        .split(/[,\n\r]+/)
        .map(p => p.trim())
        .filter(Boolean);

      const blocked = (config.restrictedPins || '')
        .split(/[,\n\r]+/)
        .map(p => p.trim())
        .filter(Boolean);

      // Parse custom JSON data
      let customData = {};
      try { customData = JSON.parse(config.customPinData || '{}'); } catch (_) {}

      // Blocked list always wins
      if (blocked.includes(pin)) {
        return Promise.resolve({ serviceable: false, city: '', state: '', cod: false, detail: '' });
      }

      const pinOverride = customData[pin];
      if (pinOverride) {
        return Promise.resolve({
          serviceable: pinOverride.serviceable !== false,
          city: pinOverride.city || '',
          state: pinOverride.state || '',
          cod: pinOverride.cod !== false,
          detail: pinOverride.message || '',
          deliveryDays: pinOverride.days,
          free: pinOverride.free === true
        });
      }

      if (config.useAllowList) {
        const found = allowed.length === 0 || allowed.includes(pin);
        return Promise.resolve({ serviceable: found, city: '', state: '', cod: true, detail: '' });
      } else {
        return Promise.resolve({ serviceable: true, city: '', state: '', cod: true, detail: '' });
      }
    }
  };

  /* ════════════════════════════════════════════════
   * ANALYTICS
   * ════════════════════════════════════════════════ */

  function trackEvent(name, data) {
    // Shopify Analytics
    try {
      if (window.analytics && window.analytics.track) {
        window.analytics.track(name, data);
      }
    } catch (_) {}

    // Google Analytics 4
    try {
      if (window.gtag) {
        window.gtag('event', name, { event_category: 'PinCodeChecker', ...data });
      }
    } catch (_) {}

    // GTM dataLayer
    try {
      if (window.dataLayer) {
        window.dataLayer.push({ event: name, ...data });
      }
    } catch (_) {}

    // Facebook Pixel
    try {
      if (window.fbq) {
        window.fbq('trackCustom', name, data);
      }
    } catch (_) {}

    // Custom DOM event (for devs to hook into)
    document.dispatchEvent(new CustomEvent('pcc:' + name.toLowerCase().replace(/\s/g, '_'), {
      bubbles: true, detail: data
    }));
  }

  /* ════════════════════════════════════════════════
   * CONFETTI
   * ════════════════════════════════════════════════ */

  function launchConfetti() {
    const canvas = document.createElement('canvas');
    canvas.className = 'pcc-confetti-canvas';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const pieces = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: -10 - Math.random() * 100,
      r: 4 + Math.random() * 6,
      d: 1 + Math.random() * 2,
      color: ['#2563eb','#059669','#f59e0b','#ef4444','#8b5cf6','#ec4899'][Math.floor(Math.random() * 6)],
      tilt: Math.random() * 20 - 10,
      tiltSpeed: 0.1 + Math.random() * 0.3
    }));

    let frame;
    let done = false;

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        ctx.beginPath();
        ctx.fillStyle = p.color;
        ctx.ellipse(p.x, p.y, p.r, p.r * 0.6, p.tilt, 0, Math.PI * 2);
        ctx.fill();
        p.y += p.d * 3;
        p.x += Math.sin(p.y * 0.02) * 1.5;
        p.tilt += p.tiltSpeed;
      });
      if (pieces.some(p => p.y < canvas.height)) {
        frame = requestAnimationFrame(draw);
      } else {
        canvas.remove();
      }
    }

    draw();
    setTimeout(() => {
      cancelAnimationFrame(frame);
      canvas.remove();
    }, 3000);
  }

  /* ════════════════════════════════════════════════
   * RESULT ICONS
   * ════════════════════════════════════════════════ */

  const ICONS = {
    success: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`,
    error: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`,
    warning: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
    clock: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`
  };

  /* ════════════════════════════════════════════════
   * WIDGET CLASS
   * ════════════════════════════════════════════════ */

  class PinCodeChecker {
    constructor(widgetId, config) {
      this.widgetId = widgetId;
      this.cfg = config;
      this.el = document.getElementById(widgetId);
      if (!this.el) return;

      this._state = {
        loading: false,
        lastPin: null,
        lastResult: null,
        historyVisible: false
      };

      this._buildRefs();
      this._attachEvents();
      this._restoreFromSession();
    }

    /* ── DOM references ── */
    _buildRefs() {
      const id = this.widgetId;
      this.input = document.getElementById(`${id}-input`);
      this.btn = document.getElementById(`${id}-btn`);
      this.resultBox = document.getElementById(`${id}-result`);
      this.historyBox = document.getElementById(`${id}-history`);
      this.clearBtn = this.el.querySelector('.pcc-clear-btn');
      this.resultMsg = this.el.querySelector('.pcc-result-message');
      this.resultDetail = this.el.querySelector('.pcc-result-detail');
      this.resultIcon = this.el.querySelector('.pcc-result-icon');
      this.deliveryInfo = this.el.querySelector('.pcc-delivery-info');
      this.deliveryDate = this.el.querySelector('.pcc-delivery-date');
      this.badgesBox = this.el.querySelector('.pcc-badges');
      this.codBadge = this.el.querySelector('.pcc-badge-cod');
      this.freeBadge = this.el.querySelector('.pcc-badge-free');
      this.historyList = this.el.querySelector('.pcc-history-list');
      this.historyClearBtn = this.el.querySelector('.pcc-history-clear');
    }

    /* ── Events ── */
    _attachEvents() {
      if (!this.input || !this.btn) return;

      // Button click
      this.btn.addEventListener('click', () => this._check());

      // Input — only allow digits
      this.input.addEventListener('input', (e) => {
        let val = e.target.value.replace(/\D/g, '');
        if (val.length > this.cfg.maxDigits) val = val.slice(0, this.cfg.maxDigits);
        e.target.value = val;
        this._handleInputChange(val);
      });

      // Enter key
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this._check(); }
        if (e.key === 'Escape') { this._hideHistory(); this._clearResult(); }
      });

      // Clear button
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.clearBtn.style.display = 'none';
          this._clearResult();
          this.input.focus();
        });
      }

      // History
      if (this.historyBox) {
        // Click outside to close
        document.addEventListener('click', (e) => {
          if (!this.el.contains(e.target)) this._hideHistory();
        });
        if (this.historyClearBtn) {
          this.historyClearBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            HistoryMgr.clear();
            this._hideHistory();
          });
        }
      }

      // Show history on focus if input empty
      this.input.addEventListener('focus', () => {
        if (!this.input.value && this.cfg.showHistory) {
          this._renderHistory();
        }
      });
    }

    /* ── Input change handler ── */
    _autoCheckDebounced = debounce(function(val) {
      if (val.length >= this.cfg.minDigits) this._check();
    }, this?.cfg?.debounceMs || 600);

    _handleInputChange(val) {
      // Show / hide clear button
      if (this.clearBtn) {
        this.clearBtn.style.display = val ? 'flex' : 'none';
      }

      // Clear result when user edits
      if (val !== this._state.lastPin) this._clearResult();

      // Auto-submit
      if (this.cfg.autoSubmit && val.length === this.cfg.maxDigits) {
        this._autoCheckDebounced(val);
      }

      // Show history on empty
      if (!val && this.cfg.showHistory) {
        this._renderHistory();
      } else if (val) {
        this._hideHistory();
      }
    }

    /* ── Session restore ── */
    _restoreFromSession() {
      try {
        const saved = sessionStorage.getItem(`pcc_last_${this.cfg.sectionId}`);
        if (saved) {
          const { pin, result } = JSON.parse(saved);
          if (pin && result) {
            this.input.value = pin;
            if (this.clearBtn) this.clearBtn.style.display = 'flex';
            this._renderResult(pin, result);
          }
        }
      } catch (_) {}
    }

    /* ── Main check function ── */
    async _check() {
      const pin = this.input.value.trim();

      // Validation
      if (pin.length < this.cfg.minDigits || !/^\d+$/.test(pin)) {
        this._showInvalid();
        return;
      }

      // Avoid duplicate check
      if (pin === this._state.lastPin && this._state.lastResult) return;

      this._setLoading(true);
      this._hideHistory();

      // Check cache
      const cacheKey = `${this.cfg.apiProvider}_${pin}`;
      if (this.cfg.cacheResults) {
        const cached = MemCache.get(cacheKey);
        if (cached) {
          this._setLoading(false);
          this._processResult(pin, cached);
          return;
        }
      }

      // Check blocked list first
      const blocked = (this.cfg.restrictedPins || '')
        .split(/[,\n\r]+/).map(p => p.trim()).filter(Boolean);
      if (blocked.includes(pin)) {
        this._setLoading(false);
        const result = { serviceable: false, city: '', state: '', cod: false, detail: '' };
        this._processResult(pin, result);
        return;
      }

      // Call API
      try {
        let result;

        if (this.cfg.apiProvider === 'custom') {
          result = await API.custom(pin, this.cfg.apiKey, this.cfg);
        } else if (API[this.cfg.apiProvider]) {
          result = await API[this.cfg.apiProvider](pin, this.cfg.apiKey, this.cfg);
        } else {
          throw new Error(`Unknown provider: ${this.cfg.apiProvider}`);
        }

        // Cache it
        if (this.cfg.cacheResults) {
          MemCache.set(cacheKey, result, this.cfg.cacheDuration || 60);
        }

        this._processResult(pin, result);

      } catch (err) {
        console.warn('[PinCodeChecker] API error:', err.message);
        this._handleApiError(pin, err);
      } finally {
        this._setLoading(false);
      }
    }

    /* ── Process & render result ── */
    _processResult(pin, result) {
      this._state.lastPin = pin;
      this._state.lastResult = result;

      // Save to session
      try {
        sessionStorage.setItem(`pcc_last_${this.cfg.sectionId}`, JSON.stringify({ pin, result }));
      } catch (_) {}

      // Add to history
      if (this.cfg.showHistory && result.serviceable) {
        HistoryMgr.add(pin, result.city || result.state, this.cfg.historyMax || 5);
      }

      // Analytics
      if (this.cfg.enableAnalytics) {
        trackEvent('PIN Check', {
          pin,
          serviceable: result.serviceable,
          city: result.city,
          state: result.state,
          provider: this.cfg.apiProvider,
          product_id: this.cfg.productId
        });
      }

      this._renderResult(pin, result);

      // Confetti!
      if (result.serviceable && this.cfg.showConfetti) {
        launchConfetti();
      }
    }

    /* ── Render result UI ── */
    _renderResult(pin, result) {
      const resultBox = this.resultBox;
      resultBox.className = 'pcc-result';

      // Animation class
      const animMap = { slide: 'pcc-anim-slide', fade: 'pcc-anim-fade', pop: 'pcc-anim-pop' };
      const animClass = animMap[this.cfg.animationStyle] || 'pcc-anim-slide';

      if (result.serviceable) {
        resultBox.classList.add('pcc-result-success', animClass);
        this.input.classList.remove('pcc-input-error');
        this.input.classList.add('pcc-input-success');
        this.resultIcon.innerHTML = ICONS.success;

        // Message
        const msg = (this.cfg.successMsg || 'We deliver to {pin}!')
          .replace('{pin}', pin)
          .replace('{city}', result.city || pin)
          .replace('{state}', result.state || '');
        this.resultMsg.textContent = msg;
        this.resultDetail.textContent = result.detail || '';

        // Delivery date
        if (this.cfg.showDeliveryDate && this.deliveryInfo && this.deliveryDate) {
          const daysMin = result.deliveryDays || this.cfg.deliveryDaysMin || 3;
          const daysMax = result.deliveryDays ? result.deliveryDays + 2 : (this.cfg.deliveryDaysMax || 7);
          const today = new Date();
          const dateMin = addDays(today, daysMin);
          const dateMax = addDays(today, daysMax);
          const dateStr = daysMin === daysMax
            ? formatDate(dateMin)
            : `${formatDate(dateMin)} – ${formatDate(dateMax)}`;
          this.deliveryDate.textContent = dateStr;
          this.deliveryInfo.style.display = 'flex';
        }

        // Badges
        if (this.cfg.showCodBadge && this.badgesBox) {
          this.badgesBox.style.display = 'flex';
          if (this.codBadge) {
            const cartTotal = parseFloat(this.cfg.cartTotal) || 0;
            const codMin = parseFloat(this.cfg.codThreshold) || 0;
            const showCod = result.cod !== false && cartTotal >= codMin;
            this.codBadge.style.display = showCod ? 'inline-flex' : 'none';
          }
          if (this.freeBadge && this.cfg.showFreeDeliveryBadge) {
            const cartTotal = parseFloat(this.cfg.cartTotal) || 0;
            const freeMin = parseFloat(this.cfg.freeDeliveryThreshold) || 0;
            const showFree = result.free === true || (freeMin === 0 || cartTotal >= freeMin);
            this.freeBadge.style.display = showFree ? 'inline-flex' : 'none';
          }
        }

      } else {
        resultBox.classList.add('pcc-result-error', animClass);
        this.input.classList.remove('pcc-input-success');
        this.input.classList.add('pcc-input-error');
        this.resultIcon.innerHTML = ICONS.error;

        const msg = (this.cfg.errorMsg || 'Sorry, we don\'t deliver to {pin}.')
          .replace('{pin}', pin);
        this.resultMsg.textContent = msg;
        this.resultDetail.textContent = result.detail || '';

        if (this.deliveryInfo) this.deliveryInfo.style.display = 'none';
        if (this.badgesBox) this.badgesBox.style.display = 'none';
      }

      resultBox.style.display = 'block';
    }

    /* ── API Error handling ── */
    _handleApiError(pin, err) {
      const mode = this.cfg.fallbackMode || 'show_error';

      if (mode === 'allow_all') {
        // Treat as serviceable
        const result = { serviceable: true, city: '', state: '', cod: true, detail: 'Network issue — assuming serviceable' };
        this._processResult(pin, result);

      } else if (mode === 'block_all') {
        const result = { serviceable: false, city: '', state: '', cod: false, detail: 'Network issue — try again' };
        this._processResult(pin, result);

      } else if (mode === 'custom_list') {
        // Fall back to custom list
        API.custom(pin, this.cfg.apiKey, this.cfg).then(result => {
          this._processResult(pin, result);
        });

      } else {
        // show_error (default)
        this.resultBox.className = 'pcc-result pcc-result-warning pcc-anim-fade';
        this.resultIcon.innerHTML = ICONS.warning;
        this.resultMsg.textContent = this.cfg.networkErrorMsg || 'Could not verify. Please try again.';
        this.resultDetail.textContent = '';
        if (this.deliveryInfo) this.deliveryInfo.style.display = 'none';
        if (this.badgesBox) this.badgesBox.style.display = 'none';
        this.resultBox.style.display = 'block';
      }
    }

    /* ── Invalid PIN ── */
    _showInvalid() {
      this.input.classList.add('pcc-shake');
      this.input.addEventListener('animationend', () => {
        this.input.classList.remove('pcc-shake');
      }, { once: true });

      this.resultBox.className = 'pcc-result pcc-result-warning pcc-anim-fade';
      this.resultIcon.innerHTML = ICONS.warning;
      this.resultMsg.textContent = this.cfg.invalidMsg || `Please enter a valid ${this.cfg.minDigits}-digit PIN code.`;
      this.resultDetail.textContent = '';
      if (this.deliveryInfo) this.deliveryInfo.style.display = 'none';
      if (this.badgesBox) this.badgesBox.style.display = 'none';
      this.resultBox.style.display = 'block';
    }

    /* ── Clear result ── */
    _clearResult() {
      this._state.lastPin = null;
      this._state.lastResult = null;
      this.input.classList.remove('pcc-input-success', 'pcc-input-error');
      this.resultBox.style.display = 'none';
      if (this.deliveryInfo) this.deliveryInfo.style.display = 'none';
      if (this.badgesBox) this.badgesBox.style.display = 'none';
      try { sessionStorage.removeItem(`pcc_last_${this.cfg.sectionId}`); } catch (_) {}
    }

    /* ── Loading ── */
    _setLoading(on) {
      this._state.loading = on;
      this.btn.disabled = on;
      this.input.disabled = on;
      this.btn.classList.toggle('pcc-loading', on);
      this.btn.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    /* ── History ── */
    _renderHistory() {
      if (!this.historyBox || !this.historyList) return;
      const items = HistoryMgr.load();
      if (!items.length) return;

      this.historyList.innerHTML = items.map(item => `
        <li class="pcc-history-item" data-pin="${escapeHtml(item.pin)}" role="option" tabindex="0"
            aria-label="Search ${escapeHtml(item.pin)}${item.city ? ' — ' + escapeHtml(item.city) : ''}">
          <span class="pcc-history-item-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 .49-3.93"></path></svg>
          </span>
          <span class="pcc-history-item-pin">${escapeHtml(item.pin)}</span>
          ${item.city ? `<span class="pcc-history-item-city">${escapeHtml(item.city)}</span>` : ''}
        </li>
      `).join('');

      // Click events on history items
      this.historyList.querySelectorAll('.pcc-history-item').forEach(li => {
        const handler = () => {
          this.input.value = li.dataset.pin;
          if (this.clearBtn) this.clearBtn.style.display = 'flex';
          this._hideHistory();
          this._check();
        };
        li.addEventListener('click', handler);
        li.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') handler(); });
      });

      this.historyBox.style.display = 'block';
      this._state.historyVisible = true;
    }

    _hideHistory() {
      if (this.historyBox) this.historyBox.style.display = 'none';
      this._state.historyVisible = false;
    }
  }

  /* ════════════════════════════════════════════════
   * INITIALIZATION — find all config objects and init
   * ════════════════════════════════════════════════ */

  function initAll() {
    // Find all configs registered on window
    Object.keys(window).forEach(key => {
      if (!key.startsWith('PCC_CONFIG_')) return;
      const cfg = window[key];
      if (!cfg || !cfg.widgetId) return;

      // Avoid double-init
      const el = document.getElementById(cfg.widgetId);
      if (!el || el.dataset.pccInit) return;
      el.dataset.pccInit = '1';

      new PinCodeChecker(cfg.widgetId, cfg);
    });
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Shopify section renders (theme editor)
  document.addEventListener('shopify:section:load', () => {
    // Small delay for DOM to settle
    setTimeout(initAll, 100);
  });

  // Expose for external use
  window.PinCodeChecker = PinCodeChecker;
  window.PCCCache = MemCache;
  window.PCCHistory = HistoryMgr;

})();
